import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Info } from '../models/info.model';

@Injectable({
  providedIn: 'root',
})
export class InfoService {
  private readonly api = inject(ApiService);

  private readonly infoSignal = signal<Info | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly info = this.infoSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  readonly address = computed(() => this.infoSignal()?.address ?? '');
  readonly mapsEmbed = computed(() => this.infoSignal()?.mapsEmbed ?? '');

  /**
   * Fetch building info (singleton)
   */
  fetchInfo() {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.api.get<Info>('/info').pipe(
      tap((info) => {
        this.infoSignal.set(info);
      }),
      catchError((error) => {
        this.errorSignal.set('Failed to load info');
        this.infoSignal.set(null);
        return throwError(() => error);
      }),
      finalize(() => this.loadingSignal.set(false)),
    );
  }

  /**
   * Initialize info on app startup; swallows errors to avoid blocking bootstrap
   */
  async initialize(): Promise<void> {
    try {
      await firstValueFrom(this.fetchInfo());
    } catch {
      // Do not block app startup if info fails to load
    }
  }
}

